import { useMemo, useState } from 'react';
import useSWR from 'swr';
import { set } from 'date-fns';
import { useClient } from '../../../hooks/useClient';
import { Reservation } from '../../../types/reservation';
import { ReservationType } from '../../../types/reservationType';
import { User } from '../../../types/user';
import { middleOfDay } from '../../../utils/DateUtils';
import { ReservationForm } from '../validators';
import { ReservationEvent } from '../types';
import { getEmployeeBranchIds } from '../utils/form';
import { getBlockedEvents, getHourPickerEventTitle, isSlotAvailable } from '../utils/slots';

interface Options {
  visible: boolean;
  isModifying: boolean;
  resource: Reservation | undefined;
  reservationType: ReservationType | undefined;
  reservationTypeFromId: (reservationTypeId: string) => ReservationType | undefined;
  form: ReservationForm;
  setFormFields: (fields: DeepPartial<ReservationForm>) => void;
  /** The slot the panel opened with: the saved hours when editing, or the slot clicked in the page calendar. */
  initialTemporaryEvent: ReservationEvent | undefined;
  clearInitialTemporaryEvent: () => void;
}

/**
 * The hour the user picks in the day calendar: the other reservations of that day, the slot being drawn and whether it
 * overlaps one of them. Changing the day or the employee moves the drawn slot with it.
 */
export const useHourSelection = ({
  visible,
  isModifying,
  resource,
  reservationType,
  reservationTypeFromId,
  form,
  setFormFields,
  initialTemporaryEvent,
  clearInitialTemporaryEvent,
}: Options) => {
  const { get } = useClient();
  const [temporaryEvent, setTemporaryEvent] = useState<ReservationEvent | null>(null);

  const canSelectHour =
    visible &&
    isModifying &&
    form?.selectedDay &&
    reservationType &&
    (reservationType.hasAssignedUsers ? form?.user?.id : form?.branch?.id);
  const fetchReservationsUrl = reservationType?.hasAssignedUsers
    ? `/users/${form?.user?.id}/reservations?date=${form?.selectedDay?.toISOString()}&onlyAssigned=true`
    : `/branches/${form?.branch?.id}/reservations?date=${form?.selectedDay?.toISOString()}&reservationType=${reservationType?.id}`;
  const {
    data: existingReservations,
    isLoading: existingReservationsLoading,
    mutate: mutateExistingReservations,
  } = useSWR(canSelectHour ? fetchReservationsUrl : null, get<Reservation[]>);
  const blockedEvents = useMemo(
    () => getBlockedEvents(existingReservations, reservationTypeFromId, resource?.id) ?? [],
    [existingReservations, reservationTypeFromId, resource?.id],
  );

  const invalidHourSelection = useMemo(() => {
    if (!blockedEvents.length || (!temporaryEvent && !initialTemporaryEvent)) {
      return false;
    }
    const eventToValidate = temporaryEvent || initialTemporaryEvent;
    if (!eventToValidate) return false;
    return !isSlotAvailable(eventToValidate.start, eventToValidate.end, blockedEvents);
  }, [blockedEvents, temporaryEvent, initialTemporaryEvent]);
  const scrollTime = useMemo(() => {
    const eventToScroll = temporaryEvent || initialTemporaryEvent;
    return eventToScroll?.start ?? set(new Date(), { hours: 7, minutes: 0 });
  }, [temporaryEvent, initialTemporaryEvent]);

  const handleTemporaryEventChange = (event: ReservationEvent) => {
    setTemporaryEvent(event);
    clearInitialTemporaryEvent();
  };

  const handleHoursChange = (start: Date, end: Date) => {
    setFormFields({
      localStartDate: start.toISOString(),
      localEndDate: end.toISOString(),
    });
  };

  const handleEmployeeChange = (user: User | undefined) => {
    const branchIds = getEmployeeBranchIds(user);
    setFormFields({
      user,
      branch: form.branch && branchIds?.includes(form.branch.id) ? form.branch : undefined,
    });
    if (form?.localStartDate && form?.localEndDate) {
      handleTemporaryEventChange({
        id: 'temporary',
        title: getHourPickerEventTitle(resource, reservationType, true),
        start: new Date(form?.localStartDate),
        end: new Date(form?.localEndDate),
      });
    }
  };

  const handleDayChange = (day: Date) => {
    // The full picked date, not only the day of the month, so a day in another month or year moves the slot there (#97).
    // The hour and minutes are set again after the day: in between, the old day of the month in the new month can be a
    // spring-forward day, which would move a slot in the skipped hour by one hour.
    const toPickedDay = (value: string) => {
      const old = new Date(value);
      const pickedDate = { year: day.getFullYear(), month: day.getMonth(), date: day.getDate() };
      return set(old, { ...pickedDate, hours: old.getHours(), minutes: old.getMinutes() }).toISOString();
    };
    const newStartDate = form.localStartDate ? toPickedDay(form.localStartDate) : undefined;
    const newEndDate = form.localEndDate ? toPickedDay(form.localEndDate) : undefined;
    setFormFields({
      selectedDay: middleOfDay(day),
      localStartDate: newStartDate,
      localEndDate: newEndDate,
    });
    if (newStartDate && newEndDate) {
      handleTemporaryEventChange({
        id: 'temporary',
        title: getHourPickerEventTitle(resource, reservationType, true),
        start: new Date(newStartDate),
        end: new Date(newEndDate),
      });
    }
  };

  return {
    temporaryEvent,
    setTemporaryEvent,
    canSelectHour: !!canSelectHour,
    existingReservationsLoading,
    mutateExistingReservations,
    blockedEvents,
    invalidHourSelection,
    scrollTime,
    handleTemporaryEventChange,
    handleHoursChange,
    handleEmployeeChange,
    handleDayChange,
  };
};
